/**
 * Modal wrapper for the schema-driven MCP credential wizard (``McpCredentialForm``).
 *
 * Extracted from ``SecretsCatalog`` so BOTH surfaces render the byte-identical
 * "Connect {mcp}" dialog:
 *   1. Connections catalog — Create New / Edit a stored MCP connection.
 *   2. AgentToolPicker chip "Edit" — edit THIS instance's credential inline,
 *      without re-picking the tool (Option A, full parity — see doc TOOLS.md §7).
 *
 * The form has no scope concept — it just calls ``onSave(mcpId, credentials,
 * { label })`` / ``onTest(mcpId, credentials)`` which the parent routes to the
 * scoped catalog endpoints. ``scope`` here only drives the human "applies to…"
 * blurb.
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import McpCredentialForm from './McpCredentialForm.jsx';

/**
 * @param {{
 *   mcp: object,
 *   scope?: { kind?: string },
 *   initialLabel?: string,
 *   initialValues?: object | null,
 *   initialSecretsSet?: string[] | null,
 *   onSave: (mcpId: string, credentials: object, opts: {label?: string}) => Promise<void>,
 *   onTest: (mcpId: string, credentials: object) => Promise<{ok:boolean,message:string}>,
 *   onCancel: () => void,
 * }} props
 */
export default function McpCredentialWizardDialog({
  mcp,
  scope,
  initialLabel = '',
  initialValues = null,
  initialSecretsSet = null,
  onSave,
  onTest,
  onCancel,
}) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  if (typeof document === 'undefined') return null;
  const scopeLabel =
    scope?.kind === 'POOL' ? 'this room'
      : scope?.kind === 'NETWORK' ? 'this network (all its rooms)'
      : 'the whole workspace';
  return createPortal(
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4"
      role="dialog" aria-modal="true" aria-labelledby="mcp-cred-wizard-title"
      data-testid="secrets-catalog-mcp-wizard"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-lg border border-gray-200 bg-white shadow-xl">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 id="mcp-cred-wizard-title" className="text-sm font-semibold text-gray-900">
            Connect {mcp?.displayName || mcp?.id}
          </h2>
          <p className="mt-1 text-[11px] leading-snug text-gray-500">
            This credential applies to <span className="font-medium">{scopeLabel}</span>.
            Rooms inherit it unless they set their own.
          </p>
        </div>
        <div className="px-4 py-3">
          <McpCredentialForm
            mcp={mcp}
            status={undefined}
            canManage
            initialLabel={initialLabel}
            initialValues={initialValues}
            initialSecretsSet={initialSecretsSet}
            onSave={onSave}
            onTest={onTest}
            onClear={async () => onCancel()}
          />
        </div>
        <div className="flex justify-end border-t border-gray-100 px-4 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-200 px-3 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
